import {
  isCorpTenant,
  SHOW_POLICIES_IN_PACKAGES,
  useIsTravelApp,
} from "@capone/common";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import { PolicyDetailsModal, PolicyModalButton, isSpendAndGetOffer } from "halifax";
import React, { useContext, useMemo } from "react";
import { IIdLodgings, SelectedTravelOfferScreen } from "redmond";
import { config } from "../../../../api/config";
import { ClientContext } from "../../../../App";
import { PATH_HOTELS_AVAILABILITY } from "../../../../utils/paths";
import { PackagesShopProgressBar } from "../../../book/components";
import { PackagesSearchControl } from "../../../search/components/PackagesSearchControl";
import { PackagesSearchButton } from "../../../search/components/PackagesSearchControl/components";
import { TravelWalletDetailsBanner } from "../../../travel-wallet/components";
import { transformToStringifiedAvailabilityQuery } from "../../utils/queryStringHelpers";
import { AvailabilityFilter } from "../AvailabilityFilter";
import { AvailabilitySearchControlConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";

interface IAvailabilitySearchControlProps
  extends AvailabilitySearchControlConnectorProps {
  displaySearchOnChange?: boolean;
  packagesListEnhancementsEnabled: boolean;
  isOnMapScreen?: boolean;
}

export const AvailabilitySearchControl = (
  props: IAvailabilitySearchControlProps
) => {
  const {
    displaySearchOnChange = true,
    lodgings,
    setMapBound,
    departureDate,
    returnDate,
    searchLocation,
    travelers,
    origin,
    stopsOptions,
    setViewHotelsNearLocation,
    areRequestParametersChanged,
    areHotelRequestParametersChanged,
    resetHotelFilters,
    mapBounds,
    fareclassOptionFilter,
    packagesListEnhancementsEnabled,
    isOnMapScreen,
  } = props;

  const [isPolicyModalOpen, setIsPolicyModalOpen] = React.useState(false);
  const [showWalletBanner, setShowWalletBanner] = React.useState(true);

  const { policies } = useContext(ClientContext);

  const onShowPolicyDetailsModal = () => {
    setIsPolicyModalOpen(true);
  };

  const { isTravelApp } = useIsTravelApp();

  const bestOffer = useMemo(() => {
    const offer = lodgings.find((lodging) => !!lodging.bestOfferThisLodging)
      ?.bestOfferThisLodging;
    if (offer && isSpendAndGetOffer(offer.descriptions)) return undefined;
    return offer;
  }, [lodgings]);

  const isPolicyDescriptorsEnabled = SHOW_POLICIES_IN_PACKAGES;

  return (
    <Box className="packages-availability-search-container">
      <PackagesShopProgressBar />
      {showWalletBanner
        ? ((isCorpTenant(config.TENANT) && bestOffer) ||
            !isCorpTenant(config.TENANT)) &&
          !isOnMapScreen && (
            <TravelWalletDetailsBanner
              offer={isCorpTenant(config.TENANT) ? bestOffer : undefined}
              className="pkg-hotel-shop-offer"
              screen={SelectedTravelOfferScreen.PACKAGES_AVAILABILITY}
              showButton
              bannerTextType="shop"
              variant="full-width"
              onDismiss={() => setShowWalletBanner(false)}
            />
          )
        : null}
      <Box
        className={clsx("packages-availability-search-control", {
          "show-policy-button": isPolicyDescriptorsEnabled,
        })}
      >
        <Box className="search-components-section">
          <PackagesSearchControl
            showSearchButton={
              !!packagesListEnhancementsEnabled &&
              areRequestParametersChanged &&
              !!origin &&
              !!departureDate &&
              !!returnDate
            }
            showPackageSomeTest={false}
            showMegaMenu={false}
          />
        </Box>
        <Box className="sort-filter-section">
          {(!displaySearchOnChange || areRequestParametersChanged) &&
            origin &&
            departureDate &&
            returnDate && (
              <PackagesSearchButton
                className={clsx("packages-search-control-button", "b2b")}
                message={textConstants.SEARCH_AGAIN}
                onClick={(history) => {
                  history.push(
                    `${PATH_HOTELS_AVAILABILITY}${transformToStringifiedAvailabilityQuery(
                      origin?.id?.code?.code,
                      (searchLocation?.id as IIdLodgings).lodgingSelection
                        .searchTerm,
                      (searchLocation?.id as IIdLodgings).lodgingSelection
                        .placeId,
                      departureDate,
                      returnDate,
                      travelers.adultsCount,
                      travelers.children,
                      travelers.infants,
                      stopsOptions,
                      fareclassOptionFilter,
                      undefined,
                      isTravelApp
                    )}`
                  );
                  if (mapBounds) {
                    setMapBound(null);
                  }
                  setViewHotelsNearLocation(null);
                  if (areHotelRequestParametersChanged) {
                    resetHotelFilters();
                  }
                }}
              />
            )}
          <AvailabilityFilter
            renderOpenModalButton
            showMealPlanTypeFilter={packagesListEnhancementsEnabled}
            showDiscountFilter={packagesListEnhancementsEnabled}
            useHotelNameAutocomplete={packagesListEnhancementsEnabled}
            trackingLocation={
              packagesListEnhancementsEnabled ? "map" : undefined
            }
          />
        </Box>
      </Box>
      {isPolicyDescriptorsEnabled && (
        <>
          <Box className="policy-modal-button-container">
            <PolicyModalButton
              underline={false}
              policies={policies}
              onClick={onShowPolicyDetailsModal}
            />
          </Box>
          <PolicyDetailsModal
            policies={policies}
            isOpen={isPolicyModalOpen}
            setIsOpen={setIsPolicyModalOpen}
            productType="hotel"
          />
        </>
      )}
    </Box>
  );
};
